import React from 'react';
import { Card, Stack, Container } from '@fiverr-private/layout_components';
import { Text, Typography } from '@fiverr-private/typography';
import { ExpiredStatus, SentStatus, SignedStatus } from '@fiverr-private/statuses';
import { getContext } from '@fiverr-private/fiverr_context';
import { I18n } from '@fiverr-private/i18n-react';
import { Tooltip } from '@fiverr-private/tooltip';
import { DocumentStatus, SentTemplateData, TemplateResponseObject } from '../../api';
import { formatDateToShortWithLocale } from '../../utils/date';
import { useResizableRef } from '../../hooks/useResizableRef';
import { LegalDocsConfigurations } from '../../types';
import { DocumentActions } from './DocumentActions';

export interface SellerItemProps {
    document: SentTemplateData;
    template?: TemplateResponseObject;
    configurations?: LegalDocsConfigurations;
}

const MOBILE_RESOLUTION_BREAKPOINT = 'sm';

const TRANSLATION_PREFIX = 'legal_documents_package.seller_templates';

const StatusMap = {
    [DocumentStatus.SIGNED]: SignedStatus,
    [DocumentStatus.EXPIRED]: () => (
        <Tooltip
            size="sm"
            position="bottom-end"
            content={<I18n k={`${TRANSLATION_PREFIX}.expired_tooltip`} />}
            appendToBody
        >
            <ExpiredStatus />
        </Tooltip>
    ),
    [DocumentStatus.SENT]: SentStatus,
    [DocumentStatus.AUTO_RESPONDED]: SentStatus,
    [DocumentStatus.PENDING]: SentStatus,
};

export const SellerDocument = ({ document, template, configurations }: SellerItemProps) => {
    const { ref, compact } = useResizableRef();

    const { locale, userId } = getContext();
    const createdBy =
        userId === document.createdBy ? (
            <I18n k={`${TRANSLATION_PREFIX}.sent_by_you`} />
        ) : (
            <I18n k={`${TRANSLATION_PREFIX}.sent_by_user`} params={{ userName: document.createdByUsername }} />
        );

    const StatusComponent = StatusMap[document.currentStatus];
    return (
        <Card borderRadius="lg" ref={ref}>
            <Stack
                width="100%"
                gap="2"
                alignItems={compact ? 'flexStart' : { default: 'flexStart', [MOBILE_RESOLUTION_BREAKPOINT]: 'center' }}
                paddingX="4"
                paddingY="2"
                wrap="nowrap"
                justifyContent="spaceBetween"
                overflow="hidden"
            >
                <Stack
                    gap={compact ? '2' : { default: '2', [MOBILE_RESOLUTION_BREAKPOINT]: '2' }}
                    overflow="hidden"
                    width="100%"
                    paddingY={compact ? '1' : { default: '1', [MOBILE_RESOLUTION_BREAKPOINT]: '0' }}
                >
                    <Stack
                        alignItems="center"
                        width="100%"
                        overflow="visible"
                        wrap={compact ? 'wrap' : { default: 'wrap', [MOBILE_RESOLUTION_BREAKPOINT]: 'nowrap' }}
                        gap="2"
                    >
                        <Stack width="100%" overflow="hidden">
                            <Text overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap" size="b_sm">
                                {document.documentName}
                            </Text>
                        </Stack>

                        <Container
                            width="100%"
                            justifyContent="center"
                            overflow="hidden"
                            display={compact ? 'none' : { default: 'none', md: 'inlineFlex' }}
                        >
                            <Text overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap" size="b_sm">
                                {createdBy}
                            </Text>
                        </Container>

                        <Stack
                            width="100%"
                            justifyContent={
                                compact
                                    ? 'flexStart'
                                    : { default: 'flexStart', [MOBILE_RESOLUTION_BREAKPOINT]: 'flexEnd' }
                            }
                            overflow="hidden"
                        >
                            <Stack height="22px" alignItems="center" gap="2" overflow="hidden">
                                <Stack alignItems="center" gap="2" overflow="hidden">
                                    {StatusComponent && <StatusComponent />}
                                    <Container alignItems="center" overflow="hidden">
                                        <Typography
                                            size="b_sm"
                                            color="grey_1100"
                                            fontWeight="normal"
                                            whiteSpace="nowrap"
                                            overflow="hidden"
                                            textOverflow="ellipsis"
                                        >
                                            {formatDateToShortWithLocale(
                                                document.updatedAt || document.createdAt,
                                                locale
                                            )}
                                        </Typography>
                                    </Container>
                                </Stack>
                            </Stack>
                        </Stack>
                    </Stack>
                </Stack>
                <DocumentActions document={document} template={template} configurations={configurations} />
            </Stack>
        </Card>
    );
};
